import { useAtomValue } from 'jotai';
import { type ReactElement, useState } from 'react';

import type { MinimalCard } from '@/services/cardDatabase';
import { useDraftActions } from '../hooks/useDraftActions';
import { currentPackAtom, draftStatsAtom } from '../state/draftAtoms';

import ArchetypeIndicator from './ArchetypeIndicator';
import ColorSignals from './ColorSignals';
import DraftPoolView from './DraftPoolView';
import DraftProgress from './DraftProgress';
import DraftStatsPanel from './DraftStatsPanel';
import PackView from './PackView';
import PickRecommendation from './PickRecommendation';

export default function DraftArena(): ReactElement {
  const { session, makePick } = useDraftActions();
  const pack = useAtomValue(currentPackAtom);
  const stats = useAtomValue(draftStatsAtom);
  const [selectedCards, setSelectedCards] = useState<MinimalCard[]>([]);

  const cardsPerPick = session?.cardsPerPick ?? 1;

  const handleSelectCard = (card: MinimalCard) => {
    if (cardsPerPick === 1) {
      setSelectedCards([card]);
    } else {
      setSelectedCards((prev) => {
        const isSelected = prev.some((c) => c.id === card.id);
        if (isSelected) return prev.filter((c) => c.id !== card.id);
        if (prev.length >= cardsPerPick) return [...prev.slice(1), card];
        return [...prev, card];
      });
    }
  };

  const handleConfirmPick = () => {
    if (selectedCards.length === 0) return;
    if (cardsPerPick > 1 && selectedCards.length < cardsPerPick && pack.length >= cardsPerPick) return;
    makePick(selectedCards);
    setSelectedCards([]);
  };

  const handleDoublePick = (card: MinimalCard) => {
    if (cardsPerPick === 1) {
      makePick([card]);
      setSelectedCards([]);
    }
  };

  if (!session) return <div />;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Progress bar */}
      <DraftProgress
        packIndex={session.currentPackIndex}
        pickIndex={session.currentPickIndex}
        totalPacks={3}
        cardsInPack={pack.length}
        setName={session.setName}
      />

      {/* Main 3-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Stats panel */}
        <aside className="hidden w-64 shrink-0 overflow-y-auto border-r border-zinc-700/50 p-3 lg:block">
          <DraftStatsPanel stats={stats} />
          <hr className="my-4 border-zinc-700/50" />
          <ColorSignals />
          <hr className="my-4 border-zinc-700/50" />
          <ArchetypeIndicator />
          <hr className="my-4 border-zinc-700/50" />
          <PickRecommendation />
        </aside>

        {/* Center: Pack */}
        <main className="flex flex-1 flex-col overflow-hidden">
          <PackView
            cards={pack}
            selectedCards={selectedCards}
            cardsPerPick={cardsPerPick}
            onSelect={handleSelectCard}
            onDoublePick={handleDoublePick}
            onConfirm={handleConfirmPick}
          />
        </main>

        {/* Right: Pool */}
        <aside className="hidden w-72 shrink-0 overflow-y-auto border-l border-zinc-700/50 p-3 xl:block">
          <DraftPoolView pool={session.playerPool} />
        </aside>
      </div>

      {/* Mobile: pool count indicator */}
      <div className="flex items-center justify-between border-t border-zinc-700/50 bg-zinc-800/50 px-4 py-2 lg:hidden">
        <span className="text-xs text-zinc-400">Pool: {session.playerPool.length} cards</span>
        <span className="text-xs text-zinc-500">
          {stats.creatureCount} creatures · {stats.averageCMC.toFixed(1)} avg CMC
        </span>
      </div>
    </div>
  );
}
